import { useState, useEffect, useMemo } from "react";
import { useProject, useMeasurement } from "../../../../../hooks/query";
import { useQueryClient } from "@tanstack/react-query";

export default function useEventHandler(details: any | null) {
    const projectId = details?.Id as string | undefined;
    const queryClient = useQueryClient();
    const { useProjectUpdate, useGetEagleViewReport } = useProject();
    const { useCreate: useCreateMeasurement } = useMeasurement();

    const updateProjectSvc = useProjectUpdate();
    const readEagleViewReportSvc = useGetEagleViewReport();
    const createMeasurementSvc = useCreateMeasurement();

    const [slug, setSlug] = useState<string>(details?.Slug ?? "");
    const [projectType, setProjectType] = useState<string>(details?.ProjectType ?? "");
    const [status, setStatus] = useState<string>(details?.Status ?? "");
    const [secondarySalesmanName, setSecondarySalesmanName] = useState<string>(details?.SecondarySalesmanName ?? "");
    const [eagleViewOrderId, setEagleViewOrderId] = useState<string>(details?.EagleViewOrderId ?? "");

    // Milestone Dates
    const [startDate, setStartDate] = useState<string>(details?.StartDate ?? "");
    const [inspectionDate, setInspectionDate] = useState<string>(details?.InspectionDate ?? "");
    const [estimateDate, setEstimateDate] = useState<string>(details?.EstimateDate ?? "");
    const [permitSubmissionDate, setPermitSubmissionDate] = useState<string>(details?.PermitSubmissionDate ?? "");
    const [materialPurchaseDate, setMaterialPurchaseDate] = useState<string>(details?.MaterialPurchaseDate ?? "");
    const [jobStartDate, setJobStartDate] = useState<string>(details?.JobStartDate ?? "");
    const [completionDate, setCompletionDate] = useState<string>(details?.CompletionDate ?? "");
    const [estimatedEndDate, setEstimatedEndDate] = useState<string>(details?.EstimatedEndDate ?? "");

    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        setSlug(details?.Slug ?? "");
        setProjectType(details?.ProjectType ?? "");
        setStatus(details?.Status ?? "");
        setSecondarySalesmanName(details?.SecondarySalesmanName ?? "");
        setEagleViewOrderId(details?.EagleViewOrderId ?? "");

        setStartDate(details?.StartDate ?? "");
        setInspectionDate(details?.InspectionDate ?? "");
        setEstimateDate(details?.EstimateDate ?? "");
        setPermitSubmissionDate(details?.PermitSubmissionDate ?? "");
        setMaterialPurchaseDate(details?.MaterialPurchaseDate ?? "");
        setJobStartDate(details?.JobStartDate ?? "");
        setCompletionDate(details?.CompletionDate ?? "");
        setEstimatedEndDate(details?.EstimatedEndDate ?? "");
    }, [
        details?.Slug,
        details?.ProjectType,
        details?.Status,
        details?.SecondarySalesmanName,
        details?.EagleViewOrderId,
        details?.StartDate,
        details?.InspectionDate,
        details?.EstimateDate,
        details?.PermitSubmissionDate,
        details?.MaterialPurchaseDate,
        details?.JobStartDate,
        details?.CompletionDate,
        details?.EstimatedEndDate,
    ]);

    const hasChanges = useMemo(() => {
        if (!details) return false;
        const normalize = (val: any) => (val === null || val === undefined ? "" : String(val));
        return (
            normalize(details.Slug) !== normalize(slug) ||
            normalize(details.ProjectType) !== normalize(projectType) ||
            normalize(details.Status) !== normalize(status) ||
            normalize(details.SecondarySalesmanName) !== normalize(secondarySalesmanName) ||
            normalize(details.EagleViewOrderId) !== normalize(eagleViewOrderId) ||
            normalize(details.StartDate) !== normalize(startDate) ||
            normalize(details.InspectionDate) !== normalize(inspectionDate) ||
            normalize(details.EstimateDate) !== normalize(estimateDate) ||
            normalize(details.PermitSubmissionDate) !== normalize(permitSubmissionDate) ||
            normalize(details.MaterialPurchaseDate) !== normalize(materialPurchaseDate) ||
            normalize(details.JobStartDate) !== normalize(jobStartDate) ||
            normalize(details.CompletionDate) !== normalize(completionDate) ||
            normalize(details.EstimatedEndDate) !== normalize(estimatedEndDate)
        );
    }, [
        details,
        slug,
        projectType,
        status,
        secondarySalesmanName,
        eagleViewOrderId,
        startDate,
        inspectionDate,
        estimateDate,
        permitSubmissionDate,
        materialPurchaseDate,
        jobStartDate,
        completionDate,
        estimatedEndDate,
    ]);

    const parseFeet = (value: any): number => {
        if (typeof value !== "string" && typeof value !== "number") return 0;
        const cleaned = String(value).replace(/[^\d.]/g, "");
        return parseFloat(cleaned) || 0;
    };

    const syncEagleViewMeasurements = async (orderId: string) => {
        const report = await readEagleViewReportSvc.mutateAsync({ ReportId: orderId });
        const measurements = report?.TotalMeasurements;

        if (!measurements) {
            throw new Error(
                "The EagleView report was found, but it contains no measurement data. Please check the Order ID.",
            );
        }

        const params = {
            ProjectId: projectId,
            Area: parseFeet(measurements.AreaValue),
            Ridge: parseFeet(measurements.LengthRidge),
            Hip: parseFeet(measurements.LengthHip),
            Valley: parseFeet(measurements.LengthValley),
            Eave: parseFeet(measurements.LengthEave),
            Rake: parseFeet(measurements.LengthRake),
            "Step Flashing": "0",
            "Counter Flashing": "0",
            "Roof Vent": "0",
            "Pipe Jack": "0",
            "Furnace Vents": "0",
            "Bathroom Vents": "0",
            "Dryer Vents": "0",
            "Split Boots": "0",
            Headwall: "0",
            "Roll Metal": "0",
            "Soffit Length": "0",
            Chimney: "0",
            Skylights: "0",
            "Open Patio/Soffits": "0",
            "Satellite Dish": "0",
            "Solar Panels": "0",
        };

        await createMeasurementSvc.mutateAsync(params);
    };

    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleUpdate = async () => {
        if (!projectId || !hasChanges) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            if (!details?.EagleViewOrderId && eagleViewOrderId.trim() !== "") {
                await syncEagleViewMeasurements(eagleViewOrderId);
            }

            const payload: any = {
                Id: projectId,
                UpdatedAt: new Date().toISOString(),
            };

            const normalize = (val: any) => (val === null || val === undefined ? "" : String(val));
            if (normalize(details?.Slug) !== normalize(slug)) payload.Slug = slug;
            if (normalize(details?.ProjectType) !== normalize(projectType)) payload.ProjectType = projectType;
            if (normalize(details?.Status) !== normalize(status)) payload.Status = status;
            if (normalize(details?.SecondarySalesmanName) !== normalize(secondarySalesmanName))
                payload.SecondarySalesmanName = secondarySalesmanName;
            if (normalize(details?.EagleViewOrderId) !== normalize(eagleViewOrderId))
                payload.EagleViewOrderId = eagleViewOrderId;

            if (normalize(details?.StartDate) !== normalize(startDate)) payload.StartDate = startDate;
            if (normalize(details?.InspectionDate) !== normalize(inspectionDate))
                payload.InspectionDate = inspectionDate;
            if (normalize(details?.EstimateDate) !== normalize(estimateDate)) payload.EstimateDate = estimateDate;
            if (normalize(details?.PermitSubmissionDate) !== normalize(permitSubmissionDate))
                payload.PermitSubmissionDate = permitSubmissionDate;
            if (normalize(details?.MaterialPurchaseDate) !== normalize(materialPurchaseDate))
                payload.MaterialPurchaseDate = materialPurchaseDate;
            if (normalize(details?.JobStartDate) !== normalize(jobStartDate)) payload.JobStartDate = jobStartDate;
            if (normalize(details?.CompletionDate) !== normalize(completionDate))
                payload.CompletionDate = completionDate;
            if (normalize(details?.EstimatedEndDate) !== normalize(estimatedEndDate))
                payload.EstimatedEndDate = estimatedEndDate;

            await updateProjectSvc.mutateAsync(payload);

            // Invalidate queries to refresh the UI with new data
            queryClient.invalidateQueries({ queryKey: ["project", projectId] });
            queryClient.invalidateQueries({ queryKey: ["measurement", projectId] });

            setSuccess("Project details updated successfully");
        } catch (error: any) {
            setError(error);
        } finally {
            setSaving(false);
        }
    };

    return {
        slug,
        setSlug,
        projectType,
        setProjectType,
        status,
        setStatus,
        secondarySalesmanName,
        setSecondarySalesmanName,
        eagleViewOrderId,
        setEagleViewOrderId,
        startDate,
        setStartDate,
        inspectionDate,
        setInspectionDate,
        estimateDate,
        setEstimateDate,
        permitSubmissionDate,
        setPermitSubmissionDate,
        materialPurchaseDate,
        setMaterialPurchaseDate,
        jobStartDate,
        setJobStartDate,
        completionDate,
        setCompletionDate,
        estimatedEndDate,
        setEstimatedEndDate,
        saving,
        error,
        success,
        hasChanges,
        handleUpdate,
    };
}
